'use client'

import { motion } from 'framer-motion'
import { useState, useEffect } from 'react'
import { useLang } from '../context/LangContext'
import { t, productStatus } from '../lib/translations'
import Icon from '../components/Icon'

type ServerStatus = 'operational' | 'degraded' | 'down'

interface Server {
  name: string
  description: string
  status: ServerStatus
  latency: string
  uptime: string
  incidents: number
}

const servers: Server[] = [
  {
    name: 'Lexoya Website',
    description: 'Main website and documentation',
    status: 'operational',
    latency: '12ms',
    uptime: '99.99%',
    incidents: 0,
  },
  {
    name: 'Lexoya PaaS Platform (k8s)',
    description: 'Primary Kubernetes platform for application hosting',
    status: 'operational',
    latency: '8ms',
    uptime: '99.98%',
    incidents: 0,
  },
  {
    name: 'Lexoya S3',
    description: 'S3-compatible object storage service',
    status: 'operational',
    latency: '4ms',
    uptime: '99.99%',
    incidents: 0,
  },
  {
    name: 'Lexoya PaaS Platform DE (k8s)',
    description: 'Germany-based Kubernetes platform cluster',
    status: 'operational',
    latency: '8ms',
    uptime: '99.99%',
    incidents: 0,
  },
]

const statusConfig: Record<ServerStatus, { labelKey: keyof typeof productStatus; dot: string; ping: string; bg: string }> = {
  operational: {
    labelKey: 'operational',
    dot: 'bg-[#22c55e] shadow-[0_0_6px_rgba(34,197,94,0.5)]',
    ping: 'bg-[#22c55e]',
    bg: 'bg-[#22c55e]/10 text-[#22c55e]',
  },
  degraded: {
    labelKey: 'degraded',
    dot: 'bg-[#f59e0b] shadow-[0_0_6px_rgba(245,158,11,0.5)]',
    ping: 'bg-[#f59e0b]',
    bg: 'bg-[#f59e0b]/10 text-[#f59e0b]',
  },
  down: {
    labelKey: 'down',
    dot: 'bg-[#ef4444] shadow-[0_0_6px_rgba(239,68,68,0.5)]',
    ping: 'bg-[#ef4444]',
    bg: 'bg-[#ef4444]/10 text-[#ef4444]',
  },
}

type IncidentSeverity = 'down' | 'degraded'
type IncidentUpdateStatus = 'investigating' | 'identified' | 'fixed' | 'resolved'

interface IncidentUpdate {
  time: string
  status: IncidentUpdateStatus
  message: Record<'en'|'fa'|'ar', string>
}

interface RecentIncident {
  id: string
  title: Record<'en'|'fa'|'ar', string>
  description: Record<'en'|'fa'|'ar', string>
  severity: IncidentSeverity
  affectedServices: string[]
  updates: IncidentUpdate[]
}

interface MaintenanceWindow {
  id: string
  title: Record<'en'|'fa'|'ar', string>
  description: Record<'en'|'fa'|'ar', string>
  from: string
  to: string
  affectedServices: string[]
}

const recentIncidents: RecentIncident[] = []

const maintenanceWindows: MaintenanceWindow[] = []

function getOverallStatus(servers: Server[]): { status: ServerStatus; labelKey: keyof typeof productStatus } {
  if (servers.length === 0) return { status: 'operational', labelKey: 'overallOperational' }
  const downCount = servers.filter((s) => s.status === 'down').length
  if (downCount === servers.length) return { status: 'down', labelKey: 'overallDown' }
  if (downCount > 0) return { status: 'degraded', labelKey: 'overallDown' }
  if (servers.some((s) => s.status === 'degraded')) return { status: 'degraded', labelKey: 'overallDegraded' }
  return { status: 'operational', labelKey: 'overallOperational' }
}

const statusLabelMap: Record<IncidentUpdateStatus, keyof typeof productStatus> = {
  investigating: 'investigating',
  identified: 'identified',
  fixed: 'fixed',
  resolved: 'resolved',
}

const severityLabelMap: Record<IncidentSeverity, keyof typeof productStatus> = {
  down: 'down',
  degraded: 'degraded',
}

const formatDate = (dateString: string, lang: 'en' | 'fa' | 'ar', includeTime: boolean = true) => {
  try {
    const d = new Date(dateString)
    if (isNaN(d.getTime())) return dateString
    const locale = lang === 'fa' ? 'fa-IR' : lang === 'ar' ? 'ar-SA' : 'en-US'
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {})
    }
    return new Intl.DateTimeFormat(locale, options).format(d)
  } catch {
    return dateString
  }
}

function StatusText({ status, lang }: { status: ServerStatus; lang: 'en' | 'fa' | 'ar' }) {
  if (status === 'operational') {
    return (
      <span className="flex items-center gap-1.5 text-xs text-[#22c55e] font-medium">
        <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor">
          <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z" />
        </svg>
        {t(lang, productStatus.operational)}
      </span>
    )
  }

  if (status === 'degraded') {
    return (
      <span className="flex items-center gap-1.5 text-xs text-[#f59e0b] font-medium">
        <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor">
          <path d="M6.457 1.047c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0114.082 15H1.918a1.75 1.75 0 01-1.543-2.575L6.457 1.047zM8 5a.75.75 0 01.75.75v2.5a.75.75 0 01-1.5 0v-2.5A.75.75 0 018 5zm0 5.5a1 1 0 100 2 1 1 0 000-2z" />
        </svg>
        {t(lang, productStatus.degraded)}
      </span>
    )
  }

  return (
    <span className="flex items-center gap-1.5 text-xs text-[#ef4444] font-medium">
      <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor">
        <path d="M4.646 4.646a.5.5 0 01.708 0L8 7.293l2.646-2.647a.5.5 0 01.708.708L8.707 8l2.647 2.646a.5.5 0 01-.708.708L8 8.707l-2.646 2.647a.5.5 0 01-.708-.708L7.293 8 4.646 5.354a.5.5 0 010-.708z" />
      </svg>
      {t(lang, productStatus.down)}
    </span>
  )
}

export default function StatusPage() {
  const lang = useLang()
  const overall = getOverallStatus(servers)
  const cfg = statusConfig[overall.status]
  const [currentTime, setCurrentTime] = useState('')

  useEffect(() => {
    setCurrentTime(new Date().toLocaleTimeString())
    const interval = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString())
    }, 30000)
    return () => clearInterval(interval)
  }, [])

  const hasOngoing = recentIncidents.some(inc => inc.updates.length === 0 || !['resolved', 'fixed'].includes(inc.updates[inc.updates.length - 1].status))

  return (
    <main className="relative pt-24 sm:pt-28 pb-24 sm:pb-32">
      <div className="absolute inset-0 dot-pattern pointer-events-none opacity-40" />

      <div className="relative max-w-3xl mx-auto px-8 sm:px-8 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-12"
        >
          <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">
            {t(lang, productStatus.heroTitle)}{' '}
            <span className="text-[#818cf8]">{t(lang, productStatus.heroHighlight)}</span>
          </h1>
          <p className="mt-4 text-[#94a3b8] text-lg">
            {t(lang, productStatus.heroDesc)}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="rounded-2xl glass overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-white/[0.04] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="relative flex h-2.5 w-2.5">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${cfg.ping} opacity-75`} />
                <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${cfg.dot}`} />
              </span>
              <span className="text-sm font-medium">{t(lang, productStatus[overall.labelKey])}</span>
            </div>
            <span className="text-xs text-[#64748b]">
              {currentTime && `${t(lang, productStatus.lastChecked)} ${currentTime}`}
            </span>
          </div>

          <div className="divide-y divide-white/[0.04]">
            {servers.map((svc, i) => {
              const scfg = statusConfig[svc.status]
              return (
                <motion.div
                  key={svc.name}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 + i * 0.06 }}
                  className="flex items-center gap-4 px-6 py-4 hover:bg-white/[0.02] transition-colors"
                >
                  <div className={`w-2 h-2 rounded-full ${scfg.dot} shrink-0`} />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-[#e2e8f0] font-medium truncate">
                        {svc.name}
                      </span>
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${scfg.bg} shrink-0`}>
                        {t(lang, productStatus[scfg.labelKey])}
                      </span>
                    </div>
                    <p className="text-xs text-[#64748b] mt-0.5 truncate">
                      {svc.description}
                    </p>
                  </div>

                  <div className="flex items-center shrink-0">
                    <StatusText status={svc.status} lang={lang} />
                  </div>
                </motion.div>
              )
            })}
          </div>


        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="mt-8 rounded-2xl glass overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-white/[0.04] flex items-center gap-2">
            <svg className="w-4 h-4 text-[#818cf8]" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 3.5a.5.5 0 01.5.5v4a.5.5 0 01-.5.5H4a.5.5 0 010-1h3.5V4a.5.5 0 01.5-.5z" />
              <path d="M8 0a8 8 0 110 16A8 8 0 018 0zM1.5 8a6.5 6.5 0 1013 0 6.5 6.5 0 00-13 0z" />
            </svg>
            <h2 className="text-sm font-medium text-[#e2e8f0]">{t(lang, productStatus.plannedMaintenance)}</h2>
          </div>
          <div className="divide-y divide-white/[0.04]">
            {maintenanceWindows.length === 0 ? (
              <div className="px-6 py-8 text-center">
                <p className="text-sm text-[#94a3b8]">{t(lang, productStatus.noMaintenance)}</p>
              </div>
            ) : (
              maintenanceWindows.map((mw) => (
                <div key={mw.id} className="px-6 py-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[#818cf8]/10 text-[#818cf8] shrink-0">
                      {t(lang, productStatus.scheduled)}
                    </span>
                    <span className="text-sm font-medium text-[#e2e8f0]">{t(lang, mw.title)}</span>
                  </div>
                  <p className="text-xs text-[#94a3b8] mb-4">{t(lang, mw.description)}</p>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-3 text-xs text-[#64748b]">
                    <span className="flex items-center">
                      {t(lang, productStatus.from)} <span suppressHydrationWarning className={`bg-white/[0.06] text-[#e2e8f0] px-1.5 py-0.5 rounded-md mx-1.5 ${lang === 'en' ? 'font-mono' : ''}`}>{formatDate(mw.from, lang)}</span>
                    </span>
                    <span className="flex items-center">
                      {t(lang, productStatus.to)} <span suppressHydrationWarning className={`bg-white/[0.06] text-[#e2e8f0] px-1.5 py-0.5 rounded-md mx-1.5 ${lang === 'en' ? 'font-mono' : ''}`}>{formatDate(mw.to, lang)}</span>
                    </span>
                    <span className="flex items-center">
                      {t(lang, productStatus.affected)} <span className="text-[#94a3b8] mx-1.5">{mw.affectedServices.join(', ')}</span>
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </motion.div>

        {recentIncidents.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8 rounded-2xl glass overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-white/[0.04] flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                {hasOngoing && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#ef4444] opacity-75" />}
                <span className={`relative inline-flex rounded-full h-2 w-2 ${hasOngoing ? 'bg-[#ef4444]' : 'bg-[#64748b]'}`} />
              </span>
              <h2 className="text-sm font-medium text-[#e2e8f0]">{t(lang, productStatus.recentIncidents)}</h2>
            </div>
            <div className="divide-y divide-white/[0.04]">
              {recentIncidents.map((incident) => {
                const lastUpdate = incident.updates.length > 0 ? incident.updates[incident.updates.length - 1] : null;
                const startDate = incident.updates.length > 0 ? incident.updates[0].time : null;
                return (
                <div key={incident.id} className="px-6 py-5">
                  {startDate && <div suppressHydrationWarning className="text-xs text-[#94a3b8] mb-1.5">{formatDate(startDate, lang, false)}</div>}
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    {lastUpdate && (
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                        ['resolved', 'fixed'].includes(lastUpdate.status) ? 'text-[#22c55e] bg-[#22c55e]/10' :
                        lastUpdate.status === 'identified' ? 'text-[#f59e0b] bg-[#f59e0b]/10' :
                        'text-[#ef4444] bg-[#ef4444]/10'
                      }`}>
                        {t(lang, productStatus[statusLabelMap[lastUpdate.status]])}
                      </span>
                    )}
                    <span className="text-sm font-medium text-[#e2e8f0]">{t(lang, incident.title)}</span>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                      incident.severity === 'down'
                        ? 'bg-[#ef4444]/10 text-[#ef4444]'
                        : 'bg-[#f59e0b]/10 text-[#f59e0b]'
                    }`}>
                      {t(lang, productStatus[severityLabelMap[incident.severity]])}
                    </span>
                  </div>
                  <p className="text-xs text-[#94a3b8] mb-4">{t(lang, incident.description)}</p>
                  <div className="text-xs text-[#64748b] mb-4 font-medium">{t(lang, productStatus.affected)} <span className="text-[#94a3b8] ml-1">{incident.affectedServices.join(', ')}</span></div>
                  <div className="relative ps-5 space-y-5">
                    <div className="absolute start-[9px] top-1 bottom-1 w-px bg-white/[0.06]" />
                    {incident.updates.map((update, ui) => (
                      <div key={ui} className="relative">
                        <div className={`absolute -start-[15px] top-1.5 w-2 h-2 rounded-full ${
                          ['resolved', 'fixed'].includes(update.status) ? 'bg-[#22c55e]' :
                          update.status === 'identified' ? 'bg-[#f59e0b]' :
                          'bg-[#ef4444]'
                        }`} />
                        <span suppressHydrationWarning className={`text-xs text-[#64748b] ${lang === 'en' ? 'font-mono' : ''}`}>{formatDate(update.time, lang)}</span>
                        <span className={`mx-2 text-[10px] font-medium px-1.5 py-0.5 rounded ${
                          ['resolved', 'fixed'].includes(update.status) ? 'text-[#22c55e] bg-[#22c55e]/10' :
                          update.status === 'identified' ? 'text-[#f59e0b] bg-[#f59e0b]/10' :
                          'text-[#ef4444] bg-[#ef4444]/10'
                        }`}>
                          {t(lang, productStatus[statusLabelMap[update.status]])}
                        </span>
                        <p className="text-xs text-[#94a3b8] mt-3">{t(lang, update.message)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )})}
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="mt-8 rounded-2xl glass p-6"
        >
          <h2 className="text-sm font-medium text-[#e2e8f0] mb-4">{t(lang, productStatus.incidentHistory)}</h2>
          <div className="space-y-4">
            {[
              { date: 'June 10, 2026', title: t(lang, productStatus.noIncidents), resolved: true, incident: false },
              { date: 'June 9, 2026', title: t(lang, productStatus.noIncidents), resolved: true, incident: false },
              { date: 'June 8, 2026', title: t(lang, productStatus.noIncidents), resolved: true, incident: false },
              { date: 'June 7, 2026', title: t(lang, productStatus.noIncidents), resolved: true, incident: false },
              { date: 'June 6, 2026', title: t(lang, productStatus.noIncidents), resolved: true, incident: false },
            ].map((incident, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 + i * 0.05 }}
                className="flex items-center gap-3"
              >
                <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${incident.incident ? 'bg-[#eab308]' : 'bg-[#22c55e]'}`} />
                <span suppressHydrationWarning className="text-xs text-[#64748b] w-32 shrink-0">{formatDate(incident.date, lang, false)}</span>
                <span className="text-xs text-[#94a3b8]">{incident.title}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </main>
  )
}
